import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead, { SEO_CONFIG } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { 
  Search, FileCheck, Users, Building, HandCoins, CheckCircle, 
  ArrowRight, Shield, Clock, Zap, Eye, Scale, Award, TrendingUp,
  Home, UserCheck, Gavel
} from 'lucide-react';

const HowItWorksPage = () => {

  const buyerSteps = [
    {
      icon: Search,
      number: '01',
      title: 'Esplora gli Immobili',
      description: 'Naviga il catalogo di immobili verificati. Ogni proprietà include documentazione completa, foto professionali e valutazione OMI certificata.'
    },
    {
      icon: Eye,
      number: '02',
      title: 'Prenota una Visita',
      description: 'Richiedi una visita gratuita all\'immobile che ti interessa. L\'agente certificato ti accompagnerà e risponderà a tutte le tue domande.'
    },
    {
      icon: FileCheck,
      number: '03',
      title: 'Richiedi Partecipazione',
      description: 'Carica i documenti richiesti e versa la caparra di garanzia. La tua richiesta sarà verificata dall\'agente immobiliare.'
    },
    {
      icon: UserCheck,
      number: '04',
      title: 'Ottieni l\'Abilitazione',
      description: 'Una volta verificata la documentazione, sarai ufficialmente abilitato a partecipare alla Gara di Vendita.'
    },
    {
      icon: Gavel,
      number: '05',
      title: 'Partecipa alla Gara',
      description: 'Il giorno della gara, fai le tue offerte in tempo reale. Ogni offerta deve rispettare il rilancio minimo prestabilito.'
    },
    {
      icon: HandCoins,
      number: '06',
      title: 'Aggiudicazione',
      description: 'Se la tua offerta è la più alta e supera il prezzo minimo, l\'immobile è tuo! Altrimenti, la caparra ti viene restituita integralmente.'
    }
  ];

  const sellerSteps = [
    {
      icon: Home,
      number: '01',
      title: 'Carica il tuo Immobile',
      description: 'Inserisci i dati del tuo immobile: foto, planimetrie, certificato APE e documenti catastali. Il processo è guidato e intuitivo.'
    },
    {
      icon: Users,
      number: '02',
      title: 'Ricevi Proposte',
      description: 'Gli agenti certificati WEQASA invieranno le loro proposte di collaborazione. Confronta le offerte e scegli il professionista ideale.'
    },
    {
      icon: FileCheck,
      number: '03',
      title: 'Firma il Mandato',
      description: 'Firma il mandato digitale con OTP sicuro. L\'agente si occuperà di tutto: dalla promozione alla gestione delle visite.'
    },
    {
      icon: TrendingUp,
      number: '04',
      title: 'Gara di Vendita',
      description: 'L\'immobile viene messo in gara. Acquirenti qualificati competono al rialzo per aggiudicarsi la tua proprietà.'
    },
    {
      icon: Award,
      number: '05',
      title: 'Vendita Conclusa',
      description: 'Una volta superato il prezzo minimo, la vendita è conclusa. Ricevi il pagamento in modo sicuro e trasparente.'
    }
  ];

  const features = [
    {
      icon: Scale,
      title: 'Prezzo Base OMI',
      description: 'La gara parte da un prezzo calcolato sui dati ufficiali dell\'Osservatorio del Mercato Immobiliare.',
      stat: '-15%',
      statLabel: 'rispetto al mercato'
    },
    {
      icon: Shield,
      title: 'Prezzo Minimo',
      description: 'Il venditore stabilisce un prezzo minimo riservato sotto il quale non è obbligato a vendere.',
      stat: 'Riservato',
      statLabel: 'solo per il venditore'
    },
    {
      icon: TrendingUp,
      title: 'Rilancio Minimo',
      description: 'Ogni nuova offerta deve superare la precedente di almeno l\'importo del rilancio stabilito.',
      stat: '€1.000+',
      statLabel: 'per ogni offerta'
    },
    {
      icon: Zap,
      title: 'Tempo Reale',
      description: 'Le offerte vengono aggiornate istantaneamente. Tutti i partecipanti vedono lo stesso prezzo.',
      stat: 'Live',
      statLabel: 'aggiornamenti istantanei'
    }
  ];

  const benefits = [
    { icon: Shield, text: 'Transazioni sicure e garantite' },
    { icon: Eye, text: 'Massima trasparenza sul prezzo' },
    { icon: Clock, text: 'Processo rapido ed efficiente' },
    { icon: Award, text: 'Agenti certificati e verificati' },
    { icon: FileCheck, text: 'Documentazione sempre completa' },
    { icon: Scale, text: 'Valutazioni OMI certificate' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead {...SEO_CONFIG.howItWorks} />
      <Header />

      {/* Hero Section - Apple Style */}
      <section className="relative bg-[#F5F5F7] py-24 lg:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1D1D1F] tracking-tight mb-6">
            Come vendere casa velocemente
          </h1>
          <p className="text-xl lg:text-2xl text-[#86868B] max-w-3xl mx-auto leading-relaxed mb-10">
            Scopri il metodo WEQASA per vendere e acquistare casa al miglior prezzo, 
            senza perdere tempo. Trasparenza e sicurezza garantite.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/registrati/acquirente">
              <Button className="h-12 px-8 rounded-full bg-[#0071e3] hover:bg-[#0077ED] text-white text-base font-medium">
                Inizia come Acquirente
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/registrati/venditore">
              <Button variant="outline" className="h-12 px-8 rounded-full border-2 border-[#1D1D1F] text-[#1D1D1F] hover:bg-[#1D1D1F] hover:text-white text-base font-medium">
                Vendi il tuo Immobile
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How Gara Works - Stats */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#1D1D1F] tracking-tight mb-4">
              La Gara di Vendita
            </h2>
            <p className="text-lg text-[#86868B] max-w-2xl mx-auto">
              Un meccanismo innovativo che garantisce il miglior prezzo per tutti
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-[#F5F5F7] rounded-3xl p-8 text-center hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-shadow duration-300"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white shadow-sm mb-6">
                  <feature.icon className="h-7 w-7 text-[#1D1D1F]" strokeWidth={1.5} />
                </div>
                <div className="text-3xl font-bold text-[#0071e3] mb-1">{feature.stat}</div>
                <div className="text-sm text-[#86868B] mb-4">{feature.statLabel}</div>
                <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">{feature.title}</h3>
                <p className="text-[15px] text-[#86868B] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Buyer Process */}
      <section className="py-24 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#0071e3] mb-6">
              <Users className="h-8 w-8 text-white" strokeWidth={1.5} />
            </div>
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#1D1D1F] tracking-tight mb-4">
              Per gli Acquirenti
            </h2>
            <p className="text-lg text-[#86868B] max-w-2xl mx-auto">
              Trova la casa dei tuoi sogni con un processo trasparente e sicuro
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {buyerSteps.map((step, index) => (
              <div 
                key={index} 
                className="bg-white rounded-3xl p-8 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-all duration-300 group"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-[#F5F5F7] flex items-center justify-center group-hover:bg-[#0071e3] transition-colors duration-300">
                    <step.icon className="h-6 w-6 text-[#1D1D1F] group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <span className="text-4xl font-bold text-[#E5E5EA]">{step.number}</span>
                </div>
                <h3 className="text-xl font-semibold text-[#1D1D1F] mb-3">{step.title}</h3>
                <p className="text-[15px] text-[#86868B] leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seller Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#0071e3] mb-6">
              <Home className="h-8 w-8 text-white" strokeWidth={1.5} />
            </div>
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#1D1D1F] tracking-tight mb-4">
              Per i Venditori
            </h2>
            <p className="text-lg text-[#86868B] max-w-2xl mx-auto">
              Vendi al miglior prezzo con la competizione al rialzo
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {sellerSteps.map((step, index) => (
              <div 
                key={index} 
                className="bg-[#F5F5F7] rounded-3xl p-6 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-all duration-300 group"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center group-hover:bg-[#0071e3] transition-colors duration-300">
                    <step.icon className="h-5 w-5 text-[#1D1D1F] group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <span className="text-3xl font-bold text-[#D1D1D6]">{step.number}</span>
                </div>
                <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">{step.title}</h3>
                <p className="text-[14px] text-[#86868B] leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-[#1D1D1F]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold text-white tracking-tight mb-4">
              Perché Scegliere WEQASA
            </h2>
            <p className="text-lg text-[#A1A1A6] max-w-2xl mx-auto">
              Vantaggi esclusivi per acquirenti e venditori
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="flex items-center space-x-4 bg-white/5 rounded-2xl p-6 border border-white/10"
              >
                <div className="w-12 h-12 rounded-xl bg-[#0071e3] flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="h-6 w-6 text-white" strokeWidth={1.5} />
                </div>
                <p className="text-lg text-white font-medium">{benefit.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-semibold text-[#1D1D1F] tracking-tight mb-6">
            Pronto a Iniziare?
          </h2>
          <p className="text-lg text-[#86868B] mb-10 max-w-2xl mx-auto">
            Unisciti a migliaia di utenti che hanno già scelto WEQASA per comprare e vendere immobili in modo trasparente.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/immobili">
              <Button className="h-14 px-10 rounded-full bg-[#0071e3] hover:bg-[#0077ED] text-white text-lg font-medium">
                Esplora gli Immobili
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contatti">
              <Button variant="outline" className="h-14 px-10 rounded-full border-2 border-[#86868B] text-[#1D1D1F] hover:border-[#1D1D1F] text-lg font-medium">
                Contattaci
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
